/// <reference lib="dom" />
/**
 * Tileset tool "Tile cases" tab: every case a style's dirt (level layer) and grass (foreground) can
 * show, the sprites linked to each by hand, drawing templates for the missing ones and linking a
 * drawn sheet back to its cases. Written with createElement (no JSX), see TilesetTool.
 */
import { createElement as h, useEffect, useMemo, useState } from 'react';
import { gameData } from '../data/gameData.ts';
import { compiledStyle } from './index.ts';
import { learnedChoices } from './runtime.ts';
import { NB2, exactNeighbours, generalCase, isPartialCase, mirrorCase, parseCase, toneMask, type CaseLayer } from './cases.ts';
import { LINE_S } from '../model/format.ts';
import { caseCatalog, type CaseRow, type CaseTier } from './caseList.ts';
import { EMPTY, FULL, composeFlags } from './shapes.ts';
import { ALL_DIRECTIONS, FILL, type CaseTemplate, type StyleOverride, type TerrainStyleData } from './types.ts';
import { Btn, ChoiceList, ChoiceThumb, Num, Toggle, drawSpriteAt, shapePoly, spriteTable, texImage, useCanvas, variantPe } from './ttShared.ts';

const PAGE = 36;
const NB_POS: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]];

const flipChoice = (c: number) => (c < 0 ? c : (c & ~7) | composeFlags(c & 7, 1));
export const flipChoices = (arr: number[]) => arr.map((v, i) => (i % 2 ? v : flipChoice(v)));

// ------------------------------------------------------------------ case drawing

type Seg = [number, number, number, number];

/**
 * Line segments of a cell's outline that face the air in a case (cell units, y up), and open inside corners.
 * A partly grassy cell's grass case lists its bare lines and corners apart.
 */
export function outlineSegments(key: string, own: number): { lines: Seg[]; dots: [number, number][]; bare: Seg[]; bareDots: [number, number][] } {
  const c = parseCase(key);
  const lines: Seg[] = [], bare: Seg[] = [];
  const dots: [number, number][] = [], bareDots: [number, number][] = [];
  if (!c) return { lines, dots, bare, bareDots };
  const g = c.grass;
  c.sides.forEach((bits, side) => {
    for (let half = 0; half < 2; half++) {
      if (!((bits >> half) & 1)) continue;
      const to = g && !(g.lines & (1 << side)) && !((g.halves >> (2 * side + half)) & 1) ? bare : lines;
      const a = half * 0.5, b = a + 0.5;
      if (side === 0) to.push([0, a, 0, b]);
      if (side === 1) to.push([1, a, 1, b]);
      if (side === 2) to.push([a, 0, b, 0]);
      if (side === 3) to.push([a, 1, b, 1]);
    }
  });
  if (own !== FULL) {
    const p = shapePoly(own);
    const onSide = (x: number, y: number, x2: number, y2: number) => (x === x2 && (x === 0 || x === 1)) || (y === y2 && (y === 0 || y === 1));
    const to = g && !(g.lines & LINE_S) ? bare : lines;
    const ends = g ? (g.halves >> 8) & 3 : 0;
    for (let i = 0; i < p.length; i++) {
      const [x, y] = p[i], [x2, y2] = p[(i + 1) % p.length];
      if (onSide(x, y, x2, y2)) continue;
      if (to === lines || !ends) {
        to.push([x, y, x2, y2]);
        continue;
      }
      // the half at the end the grass comes in at (bit 0 the left end)
      const [lx, ly, rx, ry] = x < x2 ? [x, y, x2, y2] : [x2, y2, x, y];
      const mx = (lx + rx) / 2, my = (ly + ry) / 2;
      (ends & 1 ? lines : bare).push([lx, ly, mx, my]);
      (ends & 2 ? lines : bare).push([mx, my, rx, ry]);
    }
  }
  [[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([x, y], k) => {
    if ((c.corners >> k) & 1) (g && !g.corners[k] ? bareDots : dots).push([x, y]);
  });
  return { lines, dots, bare, bareDots };
}

/**
 * 3×3 neighbourhood with the centre's case: the cell (brown) and its outline facing the air (blue for dirt,
 * green for grass). An exact-neighbours case draws its own neighbours, brighter and framed: they are part of it.
 */
export function drawCaseDiagram(ctx: CanvasRenderingContext2D, layer: CaseLayer, key: string, own: number, nb: number[], ox: number, oy: number, size: number) {
  const exact = exactNeighbours(key);
  if (exact) nb = exact;
  // buried interior cells are keyed by the 5×5 around them
  const n = nb.length > 8 ? 5 : 3;
  const r = (n - 1) / 2;
  const c = size / n;
  const P = (gx: number, gy: number, x: number, y: number): [number, number] => [ox + (gx + r + x) * c, oy + (r - gy + 1 - y) * c];
  const poly = (gx: number, gy: number, pts: [number, number][], fill: string) => {
    if (!pts.length) return;
    ctx.fillStyle = fill;
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(...P(gx, gy, x, y)) : ctx.moveTo(...P(gx, gy, x, y))));
    ctx.closePath();
    ctx.fill();
  };
  ctx.fillStyle = '#20232c';
  ctx.fillRect(ox, oy, size, size);
  const pos = n === 5 ? [...NB_POS, ...NB2] : NB_POS;
  const tone = toneMask(key);
  for (let i = 0; i < pos.length; i++) {
    const light = tone !== null && nb[i] !== EMPTY && ((tone >>> i) & 1) === 1;
    const dark = tone !== null && nb[i] !== EMPTY && !light;
    const fill = light ? '#e2c39a' : dark ? '#3a2a1c' : exact ? '#9aa3b8' : '#5d6475';
    poly(pos[i][0], pos[i][1], shapePoly(nb[i]), fill);
  }
  poly(0, 0, shapePoly(own), '#7a5636');
  if (exact) {
    ctx.strokeStyle = '#c9973c';
    ctx.lineWidth = 2;
    ctx.strokeRect(ox + 1, oy + 1, size - 2, size - 2);
  }
  ctx.strokeStyle = '#3e4352';
  ctx.lineWidth = 1;
  for (let i = 0; i <= n; i++) {
    ctx.beginPath();
    ctx.moveTo(ox + Math.round(i * c) + 0.5, oy);
    ctx.lineTo(ox + Math.round(i * c) + 0.5, oy + size);
    ctx.moveTo(ox, oy + Math.round(i * c) + 0.5);
    ctx.lineTo(ox + size, oy + Math.round(i * c) + 0.5);
    ctx.stroke();
  }
  ctx.strokeStyle = '#c9973c';
  ctx.strokeRect(ox + Math.round(r * c) + 0.5, oy + Math.round(r * c) + 0.5, Math.round(c) - 1, Math.round(c) - 1);
  {
    const { lines, dots, bare, bareDots } = outlineSegments(key, own);
    const lw = Math.max(3, size / 16);
    ctx.lineCap = 'round';
    const draw = (segs: Seg[], pts: [number, number][], colour: string, width: number) => {
      ctx.strokeStyle = ctx.fillStyle = colour;
      ctx.lineWidth = width;
      for (const [x, y, x2, y2] of segs) {
        ctx.beginPath();
        ctx.moveTo(...P(0, 0, x, y));
        ctx.lineTo(...P(0, 0, x2, y2));
        ctx.stroke();
      }
      for (const [x, y] of pts) {
        ctx.beginPath();
        ctx.arc(...P(0, 0, x, y), width * 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    draw(bare, bareDots, '#a9b0c0', Math.max(2, lw * 0.6));
    draw(lines, dots, layer === 'grass' ? '#86e04f' : '#5fd0ff', lw);
  }
}

export function CaseDiagram({ layer, caseKey, own, nb, size = 72 }: { layer: CaseLayer; caseKey: string; own: number; nb: number[]; size?: number }) {
  const ref = useCanvas([size, size], (ctx) => drawCaseDiagram(ctx, layer, caseKey, own, nb, 0, 0, size), [layer, caseKey, own, nb.join(','), size]);
  return h('canvas', {
    ref,
    width: size,
    height: size,
    className: 'tt-diagram',
    title: toneMask(caseKey) !== null
      ? 'Pale neighbours are light ground (a band tile, even where its edge pixels go dark). Dark brown neighbours are plain fill. Blue is the outline facing the air.'
      : `${layer === 'dirt' ? 'Dirt' : 'Grass'} case: ${layer === 'dirt' ? 'blue' : 'green'} = outline facing the air (dots: inside corners)${isPartialCase(caseKey) ? ', light grey = lines without grass' : ''}, dark grey = neighbours`,
  });
}

// ------------------------------------------------------------------ templates

function ensureTex(tex: string): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, 8000);
    const done = () => {
      clearTimeout(t);
      resolve();
    };
    if (texImage(tex, done)) done();
  });
}

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const canvasBlob = (c: HTMLCanvasElement) => new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('PNG export failed'))), 'image/png'));

interface TemplateCase {
  key: string;
  own: number;
  nb: number[];
  /** best learned choice (starting point), or NONE */
  learned: number;
}

/** Drawing guide at sheet resolution: the solid area and the outline facing the air of each case. */
function guideSheet(layer: CaseLayer, cases: TemplateCase[], cols: number, T: number): HTMLCanvasElement {
  const rows = Math.ceil(cases.length / cols);
  const cv = document.createElement('canvas');
  cv.width = cols * T;
  cv.height = rows * T;
  const ctx = cv.getContext('2d')!;
  cases.forEach((cs, i) => {
    const x0 = (i % cols) * T, y0 = Math.floor(i / cols) * T;
    // cell pixel (u, v) with v up from the bottom of the slot
    const px = (u: number, v: number, col: string) => {
      ctx.fillStyle = col;
      ctx.fillRect(x0 + u, y0 + T - 1 - v, 1, 1);
    };
    const p = shapePoly(cs.own);
    ctx.fillStyle = 'rgba(122,86,54,0.45)';
    ctx.beginPath();
    p.forEach(([x, y], k) => (k ? ctx.lineTo(x0 + x * T, y0 + (1 - y) * T) : ctx.moveTo(x0 + x * T, y0 + (1 - y) * T)));
    ctx.closePath();
    ctx.fill();
    const line = layer === 'grass' ? '#86e04f' : '#5fd0ff';
    const { lines, dots } = outlineSegments(cs.key, cs.own);
    for (const [x, y, x2, y2] of lines) {
      const n = T * 2;
      for (let k = 0; k <= n; k++) {
        const t = k / n;
        const u = Math.min(T - 1, Math.floor((x + (x2 - x) * t) * T)), v = Math.min(T - 1, Math.floor((y + (y2 - y) * t) * T));
        px(u, v, line);
      }
    }
    for (const [x, y] of dots) {
      const u = x ? T - 2 : 0, v = y ? T - 2 : 0;
      for (const [a, b] of [[0, 0], [1, 0], [0, 1], [1, 1]]) px(u + a, v + b, '#ff5fd2');
    }
  });
  return cv;
}

/** Sheet at its own resolution with the closest learned piece in each slot (a starting point). */
async function starterSheet(d: TerrainStyleData, ov: StyleOverride, variant: number, cases: TemplateCase[], cols: number, T: number): Promise<HTMLCanvasElement> {
  const table = spriteTable(d, ov);
  const pes = cases.map((c) => (c.learned >= 0 ? variantPe(d, table, c.learned >> 3, variant) : null));
  await Promise.all([...new Set(pes.filter(Boolean).map((p) => p!.tex))].map(ensureTex));
  const rows = Math.ceil(cases.length / cols);
  const cv = document.createElement('canvas');
  cv.width = cols * T;
  cv.height = rows * T;
  const ctx = cv.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  cases.forEach((c, i) => {
    const pe = pes[i];
    if (!pe) return;
    const x0 = (i % cols) * T, y0 = Math.floor(i / cols) * T;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x0, y0, T, T);
    ctx.clip();
    ctx.setTransform(T, 0, 0, -T, x0 + T / 2, y0 + T / 2);
    drawSpriteAt(ctx, pe, c.learned & 7, () => {});
    ctx.restore();
  });
  return cv;
}

/** Numbered overview: each case's neighbourhood diagram beside its learned piece. */
async function referenceSheet(d: TerrainStyleData, ov: StyleOverride, variant: number, layer: CaseLayer, cases: TemplateCase[], cols: number): Promise<HTMLCanvasElement> {
  const table = spriteTable(d, ov);
  const pes = cases.map((c) => (c.learned >= 0 ? variantPe(d, table, c.learned >> 3, variant) : null));
  await Promise.all([...new Set(pes.filter(Boolean).map((p) => p!.tex))].map(ensureTex));
  const D = 72, SW = D * 2 + 18, SH = D + 24;
  const rows = Math.ceil(cases.length / cols);
  const cv = document.createElement('canvas');
  cv.width = cols * SW;
  cv.height = rows * SH;
  const ctx = cv.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#16181e';
  ctx.fillRect(0, 0, cv.width, cv.height);
  cases.forEach((c, i) => {
    const x0 = (i % cols) * SW + 4, y0 = Math.floor(i / cols) * SH + 4;
    drawCaseDiagram(ctx, layer, c.key, c.own, c.nb, x0, y0, D);
    ctx.fillStyle = '#2a2e38';
    ctx.fillRect(x0 + D + 8, y0, D, D);
    const pe = pes[i];
    if (pe) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0 + D + 8, y0, D, D);
      ctx.clip();
      ctx.setTransform(D, 0, 0, -D, x0 + D + 8 + D / 2, y0 + D / 2);
      drawSpriteAt(ctx, pe, c.learned & 7, () => {});
      ctx.restore();
    }
    ctx.fillStyle = '#e9d3a3';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(`#${i + 1}  row ${Math.floor(i / cols) + 1}, col ${(i % cols) + 1}`, x0, y0 + D + 15);
  });
  return cv;
}

function bestChoice(arr: number[]): number {
  let best = -1, bw = -1;
  for (let i = 0; i + 1 < arr.length; i += 2) if (arr[i] >= 0 && arr[i + 1] > bw) [best, bw] = [arr[i], arr[i + 1]];
  return best;
}

function TemplatePanel({ styleId, d, ov, variant, layer, rows, update, onClose }: { styleId: string; d: TerrainStyleData; ov: StyleOverride; variant: number; layer: CaseLayer; rows: CaseRow[]; update: (fn: (o: StyleOverride) => void) => void; onClose: () => void }) {
  const T = useMemo(() => {
    try {
      return gameData().sprites.textures[d.tex]?.ppu || 16;
    } catch {
      return 16;
    }
  }, [d]);
  const [cols, setCols] = useState(Math.min(8, Math.max(1, rows.length)));
  const [tpl, setTpl] = useState<CaseTemplate | null>(null);
  const [busy, setBusy] = useState('');
  const st = compiledStyle(styleId);
  const cases: TemplateCase[] = useMemo(
    () =>
      rows.map((r) => {
        const l = st ? learnedChoices(st, layer, r.own, r.nb) : [];
        let best = bestChoice(l);
        if (best < 0 && l.some((c, i) => i % 2 === 0 && c === FILL)) best = (ov.fill ?? d.fill)[0] ?? -1;
        return { key: r.key, own: r.own, nb: r.nb, learned: best };
      }),
    [rows, st, layer, ov.fill, d],
  );
  const base = `${d.name.replace(/[^\w]+/g, '')}-${layer}-${new Date().toISOString().slice(0, 10)}`;
  const ensureTemplate = (): CaseTemplate => {
    if (tpl && tpl.cols === cols) return tpl;
    const t: CaseTemplate = { id: base + '-' + Math.random().toString(36).slice(2, 6), layer, keys: rows.map((r) => r.key), cols, tile: T, created: new Date().toISOString() };
    update((o) => {
      const c = (o.cases ??= {});
      c.templates = [...(c.templates ?? []).filter((x) => x.id !== tpl?.id), t];
    });
    setTpl(t);
    return t;
  };
  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    try {
      await fn();
    } finally {
      setBusy('');
    }
  };
  const preview = useCanvas(
    [Math.min(cols, rows.length) * T * 3, Math.ceil(rows.length / cols) * T * 3],
    (ctx) => {
      const g = guideSheet(layer, cases, cols, T);
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = '#20232c';
      ctx.fillRect(0, 0, g.width * 3, g.height * 3);
      ctx.drawImage(g, 0, 0, g.width * 3, g.height * 3);
      ctx.strokeStyle = '#3e4352';
      for (let i = 0; i <= cols; i++) ctx.strokeRect(i * T * 3 + 0.5, 0, 0, g.height * 3);
      for (let j = 0; j <= Math.ceil(rows.length / cols); j++) ctx.strokeRect(0, j * T * 3 + 0.5, g.width * 3, 0);
    },
    [cases, cols, T, layer],
  );
  return h(
    'div',
    { className: 'tt-modal', onClick: onClose },
    h(
      'div',
      { className: 'tt-picker', onClick: (e: { stopPropagation: () => void }) => e.stopPropagation() },
      h('div', { className: 'tt-row wrap' }, h('strong', null, `Drawing template: ${rows.length} ${layer} cases`), h('span', { className: 'tt-grow' }), h(Btn, { kind: 'ghost', onClick: onClose }, 'Close')),
      h(
        'div',
        { className: 'tt-picker-grid', style: { display: 'block' } },
        h(
          'ol',
          { className: 'tt-help', style: { paddingLeft: 18 } },
          h('li', null, `Download the guide (and the starter sheet if you want to begin from the closest existing pieces). The sheet is ${cols} × ${Math.ceil(rows.length / cols)} tiles of ${T}×${T} px, in the order of the reference sheet.`),
          h('li', null, layer === 'dirt' ? 'Draw the ground of each tile over the guide: the blue line faces the air (pink dots: inside corners). Grass is drawn separately (Grass cases); keep it out of dirt tiles.' : 'Draw the grass for each tile along the green outline (pink dots: inside corners). Draw only grass: the dirt underneath comes from the dirt layer.'),
          h('li', null, 'Save the drawing (without the guide layer) as a PNG in the Unity project under Assets/TerrainArt/, set Sprite Mode Multiple, Pixels Per Unit ' + T + ', Filter Point, and slice it Grid By Cell Size ' + T + '×' + T + '.'),
          h('li', null, 'Run npm run assets (in LevelEditor) and deploy, then use "Link a drawn sheet" here with this template. Empty slots stay unlinked.'),
        ),
        h('div', { className: 'tt-row wrap' }, 'Columns', h(Num, { value: cols, min: 1, max: 32, onChange: (v) => setCols(Math.max(1, Math.min(32, Math.round(v)))) }), busy && h('span', { className: 'tt-status' }, busy + '…')),
        h(
          'div',
          { className: 'tt-row wrap' },
          h(Btn, { kind: 'primary', disabled: !!busy, onClick: () => run('guide', async () => { const t = ensureTemplate(); download(`${t.id}-guide.png`, await canvasBlob(guideSheet(layer, cases, cols, T))); }) }, 'Guide PNG'),
          h(Btn, { disabled: !!busy, onClick: () => run('starter', async () => { const t = ensureTemplate(); download(`${t.id}-starter.png`, await canvasBlob(await starterSheet(d, ov, variant, cases, cols, T))); }) }, 'Starter PNG'),
          h(Btn, { disabled: !!busy, onClick: () => run('reference', async () => { const t = ensureTemplate(); download(`${t.id}-reference.png`, await canvasBlob(await referenceSheet(d, ov, variant, layer, cases, cols))); }) }, 'Reference PNG'),
          h(Btn, { disabled: !!busy, onClick: () => { const t = ensureTemplate(); download(`${t.id}.json`, new Blob([JSON.stringify({ style: styleId, ...t }, null, 1)], { type: 'application/json' })); } }, 'Manifest JSON'),
        ),
        tpl && h('p', { className: 'tt-help' }, `Template ${tpl.id} is recorded with this style. Save the style to keep it for linking later.`),
        h('canvas', { ref: preview, width: Math.min(cols, rows.length) * T * 3, height: Math.ceil(rows.length / cols) * T * 3, className: 'tt-preview', style: { width: 'auto', maxWidth: '100%' } }),
      ),
    ),
  );
}

// ------------------------------------------------------------------ linking a drawn sheet

interface LinkResult {
  linked: number;
  empty: number;
  unsliced: number;
}

async function linkSheet(tex: string, tpl: CaseTemplate, apply: (links: { key: string; sprite: string }[]) => void): Promise<LinkResult> {
  const def = gameData().sprites.textures[tex];
  const T = tpl.tile ?? 16;
  await ensureTex(tex);
  const img = texImage(tex, () => {});
  if (!def || !img) throw new Error('texture not loaded');
  const cv = document.createElement('canvas');
  cv.width = def.w;
  cv.height = def.h;
  const ctx = cv.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, def.w, def.h);
  const res: LinkResult = { linked: 0, empty: 0, unsliced: 0 };
  const links: { key: string; sprite: string }[] = [];
  tpl.keys.forEach((key, i) => {
    const col = i % tpl.cols, row = Math.floor(i / tpl.cols);
    const x = col * T, yTop = row * T;
    if (x + T > def.w || yTop + T > def.h) return void res.empty++;
    const px = ctx.getImageData(x, yTop, T, T).data;
    let opaque = false;
    for (let k = 3; k < px.length && !opaque; k += 4) opaque = px[k] > 8;
    if (!opaque) return void res.empty++;
    const y = def.h - yTop - T;
    const sp = def.sprites.find((s) => s.r[0] === x && s.r[1] === y && s.r[2] === T && s.r[3] === T);
    if (!sp) return void res.unsliced++;
    links.push({ key, sprite: sp.n });
    res.linked++;
  });
  apply(links);
  return res;
}

function LinkSheetPanel({ d, layer, ov, update, onClose }: { d: TerrainStyleData; layer: CaseLayer; ov: StyleOverride; update: (fn: (o: StyleOverride) => void) => void; onClose: () => void }) {
  const templates = (ov.cases?.templates ?? []).filter((t) => t.layer === layer).slice().reverse();
  const [all, setAll] = useState(false);
  const texs = useMemo(() => {
    try {
      return Object.entries(gameData().sprites.textures)
        .filter(([, t]) => all || /^Assets\/TerrainArt\//i.test(t.path))
        .map(([id, t]) => ({ id, name: t.path.replace(/^Assets\//, '') }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      return [];
    }
  }, [all]);
  const [tex, setTex] = useState('');
  const [tid, setTid] = useState(templates[0]?.id ?? '');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!texs.some((t) => t.id === tex)) setTex(texs[0]?.id ?? '');
  }, [texs, tex]);
  const go = async () => {
    const tpl = templates.find((t) => t.id === tid);
    if (!tpl || !tex) return;
    setMsg('linking…');
    try {
      const r = await linkSheet(tex, tpl, (links) =>
        update((o) => {
          const extra = (o.extraSprites ??= []);
          const table = [...d.sprites, ...extra];
          const t = ((o.cases ??= {})[layer] ??= {});
          for (const l of links) {
            let si = table.findIndex((p) => p.tex === tex && p.sprite === l.sprite);
            if (si < 0) {
              si = table.length;
              table.push({ tex, sprite: l.sprite });
              extra.push({ tex, sprite: l.sprite });
            }
            t[l.key] = [si * 8, 1];
          }
        }),
      );
      setMsg(`Linked ${r.linked} cases.` + (r.empty ? ` ${r.empty} empty slots skipped.` : '') + (r.unsliced ? ` ${r.unsliced} slots are not sliced as ${tpl.tile ?? 16}×${tpl.tile ?? 16} sprites (slice the sheet by cell size in Unity).` : '') + ' Save the style to keep the links.');
    } catch (e) {
      setMsg('Failed: ' + String(e));
    }
  };
  return h(
    'div',
    { className: 'tt-modal', onClick: onClose },
    h(
      'div',
      { className: 'tt-picker', onClick: (e: { stopPropagation: () => void }) => e.stopPropagation() },
      h('div', { className: 'tt-row wrap' }, h('strong', null, `Link a drawn ${layer} sheet`), h('span', { className: 'tt-grow' }), h(Btn, { kind: 'ghost', onClick: onClose }, 'Close')),
      h('p', { className: 'tt-help' }, 'Each tile of the drawn sheet is linked to the case at the same place in the template. Sheets come from Assets/TerrainArt/ in the Unity project (exported by npm run assets).'),
      !templates.length && h('p', { className: 'tt-help' }, 'No saved templates for this layer yet: create one with "Drawing template" first.'),
      h(
        'div',
        { className: 'tt-col' },
        h('label', { className: 'tt-row' }, 'Template', h('select', { className: 'tt-select', value: tid, onChange: (e: { target: HTMLSelectElement }) => setTid(e.target.value) }, templates.map((t) => h('option', { key: t.id, value: t.id }, `${t.id} (${t.keys.length} cases)`)))),
        h(
          'label',
          { className: 'tt-row wrap' },
          'Sheet',
          h('select', { className: 'tt-select', value: tex, onChange: (e: { target: HTMLSelectElement }) => setTex(e.target.value) }, texs.length ? texs.map((t) => h('option', { key: t.id, value: t.id }, t.name)) : h('option', { value: '' }, 'no sheets in Assets/TerrainArt/')),
          h(Toggle, { value: all, onChange: setAll, label: 'all textures' }),
        ),
        h('div', { className: 'tt-row' }, h(Btn, { kind: 'primary', disabled: !tex || !tid, onClick: go }, 'Link'), msg && h('span', { className: 'tt-status' }, msg)),
      ),
    ),
  );
}

// ------------------------------------------------------------------ the tab

function CaseCard({ row, n, layer, styleId, d, ov, variant, set, pick }: { row: CaseRow; n: number; layer: CaseLayer; styleId: string; d: TerrainStyleData; ov: StyleOverride; variant: number; set: (key: string, arr: number[] | null) => void; pick: (cb: (choice: number) => void, opts: { fill: boolean; none: boolean }) => void }) {
  const table = spriteTable(d, ov);
  const t = ov.cases?.[layer] ?? {};
  const own = t[row.key];
  const mirrorArr = t[mirrorCase(row.key)];
  const mirror = own || !ov.cases?.mirror ? null : mirrorArr;
  const arr = own ?? (mirror ? flipChoices(mirror) : null);
  const gk = generalCase(row.key);
  const generalArr = gk ? t[gk] : undefined;
  const corners = !!parseCase(row.key)?.corners;
  const st = compiledStyle(styleId);
  const learned = useMemo(() => (st ? learnedChoices(st, layer, row.own, row.nb) : []), [st, layer, row]);
  const opts = { fill: layer === 'dirt', none: layer === 'grass' };
  const meta = [
    row.levels ? `${row.levels} in levels` : 'not in levels',
    row.common ? `${row.share >= 0.001 ? (row.share * 100).toFixed(1) : '<0.1'}% of random terrain` : row.levels ? '' : 'rare',
    corners && (gk ? 'this corner shape' : 'any corner shape'),
  ].filter(Boolean);
  return h(
    'div',
    { className: `tt-card ${arr ? 'edited' : ''}` },
    h('div', { className: 'tt-card-head' }, h(CaseDiagram, { layer, caseKey: row.key, own: row.own, nb: row.nb }), h('div', { className: 'tt-dim small' }, `#${n}`, h('br'), meta.join(' · '))),
    h(
      'div',
      { className: 'tt-col', style: { flex: 1, minWidth: 0 } },
      arr
        ? h(
            'div',
            { className: 'tt-col' },
            h('div', { className: 'tt-dim small' }, mirror ? 'linked (mirror image of a linked case)' : 'linked'),
            h(ChoiceList, { d, table, variant, arr, onChange: (a) => set(row.key, a.length ? a : null), onAdd: () => pick((c) => set(row.key, [...arr, c, 1]), opts) }),
            h('div', { className: 'tt-row' }, h(Btn, { kind: 'ghost', onClick: () => set(row.key, null), title: 'use the learned pieces again' }, 'Unlink')),
          )
        : h(
            'div',
            { className: 'tt-col' },
            h('div', { className: 'tt-dim small' }, generalArr ? 'uses the tiles linked for this inside corner with any corner shape' : learned.length ? 'not linked: the learned pieces are used' : 'not linked, nothing learned'),
            h(
              'div',
              { className: 'tt-choices' },
              (generalArr ?? learned)
                .filter((_, i) => i % 2 === 0)
                .slice(0, 6)
                .map((c, i) => h('div', { key: i, className: 'tt-choice off' }, h(ChoiceThumb, { d, table, choice: c, variant, size: 36 }))),
            ),
            h(
              'div',
              { className: 'tt-row wrap' },
              generalArr && h(Btn, { onClick: () => set(row.key, [...generalArr]), title: 'give this corner shape its own copy of the any-corner tiles, to change' }, 'Own copy'),
              !generalArr && learned.length > 0 && h(Btn, { onClick: () => set(row.key, [...learned]), title: 'link the learned pieces to this case' }, 'Use learned'),
              mirrorArr && h(Btn, { onClick: () => set(row.key, flipChoices(mirrorArr)), title: 'link the flipped sprites of the mirror image (a copy: changing one later does not change the other)' }, 'Copy flipped mirror'),
              h(Btn, { onClick: () => pick((c) => set(row.key, [c, 1]), opts) }, 'Pick sprite…'),
            ),
          ),
    ),
  );
}

export function CasesTab({ styleId, d, ov, variant, update, pick }: { styleId: string; d: TerrainStyleData; ov: StyleOverride; variant: number; update: (fn: (o: StyleOverride) => void) => void; pick: (cb: (choice: number) => void, opts: { fill: boolean; none: boolean }) => void }) {
  const [layer, setLayer] = useState<CaseLayer>('grass');
  const [tier, setTier] = useState<CaseTier>('common');
  const [status, setStatus] = useState<'all' | 'missing' | 'linked'>('all');
  const [shapes, setShapes] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const [panel, setPanel] = useState<null | 'template' | 'link'>(null);
  const dirs = ov.directions ?? ALL_DIRECTIONS;
  const catalog = useMemo(() => caseCatalog(d, dirs, layer, tier), [d, dirs.s45, dirs.s21, dirs.s12, layer, tier]);
  const rows = useMemo(() => (shapes ? catalog : catalog.filter((r) => !generalCase(r.key))), [catalog, shapes]);
  const t = ov.cases?.[layer] ?? {};
  const isLinked = (k: string) => k in t || (generalCase(k) ?? '') in t || (!!ov.cases?.mirror && mirrorCase(k) in t);
  const shown = rows.filter((r) => status === 'all' || (status === 'linked') === isLinked(r.key));
  const missing = shown.filter((r) => !isLinked(r.key));
  const linkedN = rows.filter((r) => isLinked(r.key)).length;
  useEffect(() => setLimit(PAGE), [layer, tier, status, shapes]);
  const set = (key: string, arr: number[] | null) =>
    update((o) => {
      const tb = ((o.cases ??= {})[layer] ??= {});
      if (arr) tb[key] = arr;
      else delete tb[key];
    });
  const st = compiledStyle(styleId);
  const linkAllLearned = () =>
    update((o) => {
      const tb = ((o.cases ??= {})[layer] ??= {});
      for (const r of missing) {
        const l = st ? learnedChoices(st, layer, r.own, r.nb) : [];
        if (l.length) tb[r.key] = l;
      }
    });
  const setCases = (fn: (c: NonNullable<StyleOverride['cases']>) => void) => update((o) => fn((o.cases ??= {})));
  return h(
    'div',
    null,
    h(
      'div',
      { className: 'tt-row wrap' },
      h(
        'div',
        { className: 'tt-seg' },
        h('button', { className: layer === 'dirt' ? 'active' : '', onClick: () => setLayer('dirt') }, 'Dirt (level layer)'),
        h('button', { className: layer === 'grass' ? 'active' : '', onClick: () => setLayer('grass') }, 'Grass (foreground)'),
      ),
      h(
        'div',
        { className: 'tt-seg' },
        ([['levels', 'In the levels'], ['common', '+ common shapes (99%)'], ['all', '+ every possible']] as [CaseTier, string][]).map(([k, l]) => h('button', { key: k, className: tier === k ? 'active' : '', onClick: () => setTier(k) }, l)),
      ),
      h(
        'div',
        { className: 'tt-seg' },
        ([['all', 'all'], ['missing', 'not linked'], ['linked', 'linked']] as const).map(([k, l]) => h('button', { key: k, className: status === k ? 'active' : '', onClick: () => setStatus(k) }, l)),
      ),
      h(Toggle, { value: shapes, onChange: setShapes, label: 'corner shapes' }),
      h('span', { className: 'tt-dim' }, `${rows.length} cases · ${linkedN} linked`),
    ),
    h(
      'p',
      { className: 'tt-help' },
      layer === 'dirt'
        ? 'Every ground tile a cell can need, from the slopes this style supports: the blue outline faces the air (dots: inside corners where only the corner point is open). Dirt and grass have the same cases. '
        : 'Every grass tile a cell can need: the green outline faces the air (dots: inside corners where only the corner point is open). Dirt and grass have the same cases. ',
      'Inside corners (only the corner point open) are a case for each pair of surfaces meeting at the point ("this corner shape"), so a corner between slopes can differ from one between flat ground; the corner\'s "any corner shape" case covers the shapes not linked on their own ("corner shapes" off: only those). ',
      'A case and its mirror image are separate cases. Linked cases always use their sprites; the others use the learned pieces. ',
      '"+ common shapes" adds the cases that make up 99% of random terrain built with these slopes (most common first); "+ every possible" lists all cases, mostly from contrived geometry.',
    ),
    h(
      'div',
      { className: 'tt-row wrap' },
      h(Btn, { onClick: linkAllLearned, disabled: !missing.length, title: 'link each listed unlinked case to what the learned rules put there' }, `Link learned pieces (${missing.length})`),
      h(Btn, { onClick: () => setPanel('template'), disabled: !missing.length }, `Drawing template (${missing.length} not linked)…`),
      h(Btn, { onClick: () => setPanel('link') }, 'Link a drawn sheet…'),
      h('span', { className: 'tt-grow' }),
      h(Toggle, { value: !!ov.cases?.manualOnly, onChange: (v) => setCases((c) => void (v ? (c.manualOnly = true) : delete c.manualOnly)), label: 'linked cases only (no learned pieces)' }),
      h(Toggle, { value: !!ov.cases?.mirror, onChange: (v) => setCases((c) => void (v ? (c.mirror = true) : delete c.mirror)), label: 'unlinked cases use their mirror image' }),
    ),
    h(
      'div',
      { className: 'tt-cards' },
      shown.slice(0, limit).map((r) => h(CaseCard, { key: r.key, row: r, n: rows.indexOf(r) + 1, layer, styleId, d, ov, variant, set, pick })),
    ),
    shown.length > limit && h('div', { className: 'tt-row center' }, h(Btn, { onClick: () => setLimit(limit + PAGE) }, `Show more (${shown.length - limit})`)),
    panel === 'template' && h(TemplatePanel, { styleId, d, ov, variant, layer, rows: missing, update, onClose: () => setPanel(null) }),
    panel === 'link' && h(LinkSheetPanel, { d, layer, ov, update, onClose: () => setPanel(null) }),
  );
}
